<template>
  <div v-if="images"
       class="image-gallery">
    <div class="grid">
      <div v-for="(image, index) in selectedImages"
           :key="index"
           class="image-gallery__card"
           :class="{'image-gallery__card--clickable': !disableViewer}"
           @click="selectImage(index)">
        <img :src="image.url + '?width=280&format=webp&quality=75'"
             width="280px"
             :srcset="generateImageSourceSet(image)"
             :alt="image.alt">
        <i v-if="!disableViewer"
           class="fas fa-expand-arrows-alt"></i>
        <p v-if="image.label">
          {{ image.label }}
        </p>
      </div>
    </div>

    <div v-if="selectedImage && !disableViewer"
         class="image-gallery__viewer"
         @click.self="onClickOutside">
      <div class="image-gallery__viewer-container">
        <div class="image-gallery__viewer-header">
          <strong>{{ selectedImage.label }}</strong>
          <font-awesome-icon :icon="['fas', 'compress-arrows-alt']"
                             @click="selectedImageIndex = null"></font-awesome-icon>
        </div>

        <div class="image-gallery__image-container">
          <img :src="selectedImage.url + '?format=webp&quality=75'"
               :alt="selectedImage.alt">
        </div>
      </div>
      <div class="image-gallery__nav-button image-gallery__nav-button--left"
           :class="{'image-gallery__nav-button--disabled': !canSelectPrev}"
           @click="selectPrevImage">
        <font-awesome-icon :icon="['fas', 'chevron-left']"></font-awesome-icon>
      </div>
      <div class="image-gallery__nav-button image-gallery__nav-button--right"
           :class="{'image-gallery__nav-button--disabled': !canSelectNext}"
           @click="selectNextImage">
        <font-awesome-icon :icon="['fas', 'chevron-right']"></font-awesome-icon>
      </div>
    </div>

    <div class="image-gallery__pagination">
      <gallery-pagination v-model="pageIndex"
                          :items-per-page="imagesPerPage"
                          :items-count="images.length"></gallery-pagination>
    </div>
  </div>
</template>

<script>
import GalleryPagination from "~/Components/Gallery/GalleryPagination.vue";

export default {
  name: "ImageGallery",
  components: {
    GalleryPagination
  },
  props: {
    images: {
      type: Array,
      default: null
    },
    disableViewer: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      imagesPerPage: 9,
      pageIndex: 0,
      selectedImageIndex: null
    };
  },
  computed: {
    selectedImages () {
      return this.images
        .slice( this.pageIndex * this.imagesPerPage, ( this.pageIndex + 1 ) * this.imagesPerPage );
    },
    selectedImage () {
      return this.selectedImageIndex !== null ? this.images[this.selectedImageIndex] : null;
    },
    canSelectNext () {
      return this.selectedImageIndex + 1 < this.images.length;
    },
    canSelectPrev () {
      return this.selectedImageIndex - 1 >= 0;
    }
  },
  methods: {
    generateImageSourceSet ( image ) {
      return `${image.url}?width=450&format=webp&quality=75 450w,
      ${image.url}?width=270&format=webp&quality=75 270w`;
    },
    onClickOutside () {
      this.selectedImageIndex = null;
    },
    selectNextImage () {
      if ( this.canSelectNext ) {
        this.selectedImageIndex += 1;
      }
    },
    selectPrevImage () {
      if ( this.canSelectPrev ) {
        this.selectedImageIndex -= 1;
      }
    },
    selectImage ( index ) {
      if ( !this.disableViewer ) {
        const currentPageIndex = this.pageIndex * this.imagesPerPage;
        this.selectedImageIndex = currentPageIndex + index;
      }
    }
  }
};
</script>

<style lang="less" scoped>
.image-gallery {
  &__card {
    position: relative;

    &--clickable {
      &:hover {
        cursor: pointer;
      }
    }

    img {
      width: 100%;
      aspect-ratio: calc(16 / 9);
      object-fit: cover;
    }

    svg {
      position: absolute;
      right: 15px;
      top: 15px;
      color: #fff;
      filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.45));
    }

    p {
      margin: 0;
    }
  }

  &__viewer {
    outline: 1px solid red;
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 900;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;

    &-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1rem;

      svg {
        width: 24px;
        height: 24px;
        color: #000;
        transition: color .2s ease-out;

        &:hover {
          cursor: pointer;
          color: rgba(0, 0, 0, 0.75);
        }
      }
    }

    &-container {
      display: flex;
      flex-direction: column;
      padding: 3rem;
      max-width: 90vw;
      background-color: #fff;

      @media ( max-width: 767px ) {
        padding: 1.5rem;
      }

      strong {
        font-size: 24px;
      }
    }
  }

  &__image-container {
    position: relative;

    img {
      width: 100%;
      user-select: none;
    }
  }

  &__nav-button {
    width: 50px;
    height: 50px;
    border-radius: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    color: #fff;
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    opacity: 1;
    transition: background-color .2s ease-out,
                opacity .2s ease-out;

    &:not(.image-gallery__nav-button--disabled) {
      &:hover {
        cursor: pointer;
        background-color: rgba(0, 0, 0, 0.75);
      }
    }

    &--disabled {
      opacity: 0.7;
    }

    &--left {
      left: 2rem;
    }

    &--right {
      right: 2rem;
    }
  }

  .grid {
    display: grid;
    grid-gap: 1rem;
  }

  @supports (width: min(250px, 100%)) {
    .grid {
      grid-template-columns: ~"repeat( auto-fit, minmax(min(250px, 100%), 1fr))";
    }
  }
}
</style>
